// FILE: frontend/src/pages/Dashboard.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import accountService from '../services/accountService';
import transactionService from '../services/transactionService';
import Card from '../components/common/Card';
import ProfilePicture from '../components/ProfilePicture'; // ADD THIS IMPORT

const Dashboard = () => {
  const { user, logout } = useAuth(); // ADD logout here
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalBalance, setTotalBalance] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch accounts
      const accountsData = await accountService.getAccounts();
      console.log('Accounts Data:', accountsData); // DEBUG
      
      // Fetch transactions
      const transactionsData = await transactionService.getRecentTransactions();
      console.log('Transactions Data:', transactionsData); // DEBUG

      setAccounts(accountsData);
      setTransactions(transactionsData);

      // Calculate total balance
      const total = accountsData.reduce((sum, acc) => {
        const balance = parseFloat(acc.balance) || 0;
        return sum + balance;
      }, 0);
      
      console.log('Total Balance:', total); // DEBUG
      setTotalBalance(total);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* ============================================= */}
      {/* ADDED: HEADER WITH PROFILE PICTURE */}
      {/* ============================================= */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <ProfilePicture user={user} size={40} />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-neutral-900">
                  {user?.first_name || user?.username}
                </span>
                <span className="text-xs text-neutral-500">{user?.email}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-800">
            Welcome back, {user?.first_name || user?.username}!
          </h1>
          <p className="text-neutral-600 mt-2">Here's your financial overview</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-danger-100 border border-danger-400 text-danger-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Total Balance */}
        <Card className="mb-6 bg-gradient-to-r from-primary-600 to-accent-600 text-white">
          <h2 className="text-lg opacity-90 mb-2">Total Balance</h2>
          <p className="text-4xl font-bold">
            {formatCurrency(totalBalance)}
          </p>
          <p className="text-sm opacity-75 mt-2">
            Across {accounts.length} account{accounts.length !== 1 ? 's' : ''}
          </p>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Link
            to="/transfer"
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition text-center"
          >
            <div className="text-primary-600 text-4xl mb-2">💸</div>
            <h3 className="font-semibold">Transfer Money</h3>
          </Link>
          <Link
            to="/accounts"
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition text-center"
          >
            <div className="text-primary-600 text-4xl mb-2">💳</div>
            <h3 className="font-semibold">View Accounts</h3>
          </Link>
          <Link
            to="/transactions"
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition text-center"
          >
            <div className="text-primary-600 text-4xl mb-2">📊</div>
            <h3 className="font-semibold">Transaction History</h3>
          </Link>
          {/* ADDED: Profile Settings Quick Action */}
          <Link
            to="/profile"
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition text-center"
          >
            <div className="text-primary-600 text-4xl mb-2">👤</div>
            <h3 className="font-semibold">Profile Settings</h3>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Accounts */}
          <Card title="Your Accounts">
            {accounts.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <p>No accounts yet</p>
                <Link to="/accounts" className="text-primary-600 hover:underline mt-2 inline-block">
                  Create an account
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex justify-between items-center p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition"
                  >
                    <div>
                      <p className="font-semibold">{account.account_type}</p>
                      <p className="text-sm text-neutral-600">{account.account_number}</p>
                    </div>
                    <p className="text-lg font-bold text-primary-600">
                      {formatCurrency(account.balance)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          
          {/* Recent Transactions - UPDATED WITH PLUS/MINUS SIGNS */}
        <Card title="Recent Transactions">
  {transactions.length === 0 ? (
    <div className="text-center py-8 text-neutral-500">
      <p>No transactions yet</p>
    </div>
  ) : (
    <div className="space-y-3">
      {transactions.map((txn) => {
        // Determine if transaction is incoming or outgoing
        const isIncoming = txn.direction === 'incoming' || txn.is_incoming;
        const isOutgoing = txn.direction === 'outgoing' || txn.is_outgoing;
        const isDeposit = txn.transaction_type === 'DEPOSIT';
        const isWithdrawal = txn.transaction_type === 'WITHDRAWAL';
        
        // Get amount with sign
        const getAmountWithSign = () => {
          // Use backend's formatted_amount if available
          if (txn.formatted_amount) {
            return txn.formatted_amount;
          }
          // Use backend's amount_with_sign if available
          if (txn.amount_with_sign) {
            const sign = txn.amount_with_sign.charAt(0);
            const amount = txn.amount_with_sign.substring(1);
            return `${sign}$${amount}`;
          }
          // Fallback logic
          if (isIncoming || isDeposit) {
            return `+$${txn.amount}`;
          } else if (isOutgoing || isWithdrawal) {
            return `-$${txn.amount}`;
          }
          return `$${txn.amount}`;
        };

        // Get color based on direction
        const getAmountColor = () => {
          if (isIncoming || isDeposit) {
            return 'text-success-600'; // Green for incoming
          } else {
            return 'text-danger-600'; // Red for outgoing
          }
        };

        // Get description
        const getDescription = () => {
          if (txn.description) return txn.description;
          if (isIncoming) {
            return `From: ${txn.from_account_number || 'External'}`;
          } else if (isOutgoing) {
            return `To: ${txn.to_account_number || 'External'}`;
          }
          return 'Transaction';
        };

        // Get transaction type display
        const getTransactionType = () => {
          const types = {
            'TRANSFER': 'Transfer',
            'DEPOSIT': 'Deposit',
            'WITHDRAWAL': 'Withdrawal',
            'PAYMENT': 'Payment',
            'INTERNATIONAL': 'International'
          };
          return types[txn.transaction_type] || txn.transaction_type;
        };

        return (
          <div
            key={txn.id}
            className="flex justify-between items-center p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition"
          >
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  txn.transaction_type === 'DEPOSIT' ? 'bg-success-100 text-success-800' :
                  txn.transaction_type === 'WITHDRAWAL' ? 'bg-danger-100 text-danger-800' :
                  txn.transaction_type === 'TRANSFER' ? 'bg-primary-100 text-primary-800' :
                  'bg-neutral-100 text-neutral-800'
                }`}>
                  {getTransactionType()}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${
                  txn.status === 'COMPLETED' ? 'bg-success-100 text-success-800' :
                  txn.status === 'PENDING' ? 'bg-secondary-100 text-secondary-800' :
                  'bg-danger-100 text-danger-800'
                }`}>
                  {txn.status}
                </span>
              </div>
              <p className="text-sm text-neutral-600">{formatDate(txn.created_at)}</p>
              <p className="text-xs text-neutral-500 mt-1 truncate max-w-xs">
                {getDescription()}
              </p>
            </div>
            <div className="text-right">
              <p className={`text-lg font-bold ${getAmountColor()}`}>
                {getAmountWithSign()}
              </p>
              <p className="text-xs text-neutral-500 mt-1 capitalize">
                {isIncoming ? 'Incoming' : isOutgoing ? 'Outgoing' : 'Transaction'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  )}
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;